import type {
  CreateTenantData,
  Tenant,
  TenantQueryParams,
  UpdateTenantData,
  AssignUserToTenantData,
  AssignServiceToTenantData,
  UpdateTenantServiceData,
  TenantServiceOptionData,
  BulkTenantOperationData,
  CloneTenantData,
  OrganizationData,
  TenantServiceWithOptions,
  TenantServiceOption,
} from "shared/types";
import api from "../axios";

// Tenant Service Class
export class TenantService {
  // Basic CRUD Operations
  async createTenant(data: CreateTenantData): Promise<Tenant> {
    const response = await api.post<Tenant>("/tenants", data);
    return response.data;
  }

  async getTenants(params?: TenantQueryParams): Promise<Tenant[]> {
    const queryParams = new URLSearchParams();

    if (params?.skip) queryParams.append("skip", params.skip.toString());
    if (params?.take) queryParams.append("take", params.take.toString());
    if (params?.brandId) queryParams.append("brandId", params.brandId);

    const url = `/tenants${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await api.get<Tenant[]>(url);
    return response.data;
  }

  async getTenantHierarchy(brandId?: string): Promise<Tenant[]> {
    const queryParams = new URLSearchParams();
    if (brandId) queryParams.append("brandId", brandId);

    const url = `/tenants/hierarchy${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await api.get<Tenant[]>(url);
    return response.data;
  }

  async getTenantById(id: string): Promise<Tenant> {
    const response = await api.get<Tenant>(`/tenants/${id}`);
    return response.data;
  }

  async getTenantUsers(id: string): Promise<any[]> {
    const response = await api.get<any[]>(`/tenants/${id}/users`);
    return response.data;
  }

  async getTenantServices(id: string): Promise<TenantServiceWithOptions[]> {
    const response = await api.get<TenantServiceWithOptions[]>(`/tenants/${id}/services`);
    return response.data;
  }

  // Document-related operations for tenants
  async getTenantDocuments(tenantId: string): Promise<Document[]> {
    const response = await api.get<Document[]>(`/documents/tenant/${tenantId}`);
    return response.data;
  }

  async getTenantDocumentCount(tenantId: string): Promise<{ count: number }> {
    const response = await api.get<{ count: number }>(
      `/tenants/${tenantId}/documents/count`
    );
    return response.data;
  }

  async updateTenant(id: string, data: UpdateTenantData): Promise<Tenant> {
    const response = await api.patch<Tenant>(`/tenants/${id}`, data);
    return response.data;
  }

  async deleteTenant(id: string): Promise<void> {
    await api.delete(`/tenants/${id}`);
  }

  // Management Operations
  async assignUserToTenant(
    data: AssignUserToTenantData
  ): Promise<{ success: boolean }> {
    const response = await api.post<{ success: boolean }>(
      "/tenants/assign-user",
      data
    );
    return response.data;
  }

  async removeUserFromTenant(
    tenantId: string,
    userId: string
  ): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(
      `/tenants/${tenantId}/users/${userId}`
    );
    return response.data;
  }

  async assignServiceToTenant(
    data: AssignServiceToTenantData
  ): Promise<TenantServiceWithOptions> {
    const response = await api.post<TenantServiceWithOptions>(
      "/tenants/assign-service",
      data
    );
    return response.data;
  }

  async removeServiceFromTenant(
    tenantId: string,
    brandServiceId: string
  ): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(
      `/tenants/${tenantId}/services/${brandServiceId}`
    );
    return response.data;
  }

  async bulkTenantOperation(
    data: BulkTenantOperationData
  ): Promise<{ success: boolean; processedCount: number }> {
    const response = await api.post<{
      success: boolean;
      processedCount: number;
    }>("/tenants/bulk-operation", data);
    return response.data;
  }

  async cloneTenant(sourceId: string, data: CloneTenantData): Promise<Tenant> {
    const response = await api.post<Tenant>(`/tenants/clone/${sourceId}`, data);
    return response.data;
  }

  // New methods for tenant service options management
  async getTenantServiceOptions(
    tenantId: string, 
    serviceId: string
  ): Promise<TenantServiceOption[]> {
    const response = await api.get<TenantServiceOption[]>(
      `/tenants/${tenantId}/services/${serviceId}/options`
    );
    return response.data;
  }

  async updateTenantService(
    tenantId: string,
    serviceId: string,
    data: UpdateTenantServiceData
  ): Promise<TenantServiceWithOptions> {
    const response = await api.put<TenantServiceWithOptions>(
      `/tenants/${tenantId}/services/${serviceId}`,
      data
    );
    return response.data;
  }

  async upsertTenantServiceOption(
    tenantId: string,
    serviceId: string,
    key: string,
    optionData: TenantServiceOptionData
  ): Promise<TenantServiceOption> {
    const response = await api.put<TenantServiceOption>(
      `/tenants/${tenantId}/services/${serviceId}/options/${key}`,
      optionData
    );
    return response.data;
  }

  async deleteTenantServiceOption(
    tenantId: string,
    serviceId: string,
    key: string
  ): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(
      `/tenants/${tenantId}/services/${serviceId}/options/${key}`
    );
    return response.data;
  }

  // Utility Methods
  async getTenantsByBrand(brandId: string): Promise<Tenant[]> {
    const response = await api.get<Tenant[]>(`/tenants?brandId=${brandId}`);
    return response.data;
  }

  async getActiveTenants(brandId?: string): Promise<Tenant[]> {
    const queryParams = new URLSearchParams();
    queryParams.append("isActive", "true");
    if (brandId) queryParams.append("brandId", brandId);

    const url = `/tenants?${queryParams.toString()}`;
    const response = await api.get<Tenant[]>(url);
    return response.data;
  }

  async getTenantChildren(parentId: string): Promise<Tenant[]> {
    const response = await api.get<Tenant[]>(`/tenants/${parentId}/children`);
    return response.data;
  }

  async websiteOrigination(data: {
    website: string;
    organization_name?: string;
  }): Promise<OrganizationData> {
    const response = await api.post<any>("/tenants/website-origination", data);
    return response.data;
  }

  // ============= TENANT SERVICE CRUD APIs =============

  // Create new tenant service
  async createTenantService(data: {
    tenantId: string;
    brandServiceId: string;
    name: string;
    description?: string;
    options?: TenantServiceOptionData[];
  }): Promise<TenantServiceWithOptions> {
    const response = await api.post<TenantServiceWithOptions>("/tenants/services", data);
    return response.data;
  }

  // Get all tenant services with filtering
  async getAllTenantServices(params?: {
    tenantId?: string;
    brandServiceId?: string;
    isActive?: boolean;
    skip?: number;
    take?: number;
  }): Promise<TenantServiceWithOptions[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.tenantId) queryParams.append("tenantId", params.tenantId);
    if (params?.brandServiceId) queryParams.append("brandServiceId", params.brandServiceId);
    if (params?.isActive !== undefined) queryParams.append("isActive", params.isActive.toString());
    if (params?.skip) queryParams.append("skip", params.skip.toString());
    if (params?.take) queryParams.append("take", params.take.toString());

    const url = `/tenants/services${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await api.get<TenantServiceWithOptions[]>(url);
    return response.data;
  }

  // Get specific tenant service by ID
  async getTenantServiceById(serviceId: string): Promise<TenantServiceWithOptions> {
    const response = await api.get<TenantServiceWithOptions>(`/tenants/services/${serviceId}`);
    return response.data;
  }

  // Update tenant service by ID
  async updateTenantServiceById(
    serviceId: string,
    data: UpdateTenantServiceData
  ): Promise<TenantServiceWithOptions> {
    const response = await api.put<TenantServiceWithOptions>(
      `/tenants/services/${serviceId}`,
      data
    );
    return response.data;
  }

  // Delete tenant service by ID
  async deleteTenantServiceById(serviceId: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/tenants/services/${serviceId}`);
    return response.data;
  }

  // ============= TENANT SERVICE OPTIONS CRUD APIs =============

  // Get all tenant service options with filtering
  async getAllTenantServiceOptions(params?: {
    tenantServiceId?: string;
    category?: string;
    isActive?: boolean;
    isRequired?: boolean;
    skip?: number;
    take?: number;
  }): Promise<TenantServiceOption[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.tenantServiceId) queryParams.append("tenantServiceId", params.tenantServiceId);
    if (params?.category) queryParams.append("category", params.category);
    if (params?.isActive !== undefined) queryParams.append("isActive", params.isActive.toString());
    if (params?.isRequired !== undefined) queryParams.append("isRequired", params.isRequired.toString());
    if (params?.skip) queryParams.append("skip", params.skip.toString());
    if (params?.take) queryParams.append("take", params.take.toString());

    const url = `/tenants/service-options${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await api.get<TenantServiceOption[]>(url);
    return response.data;
  }

  // Get specific tenant service option by ID
  async getTenantServiceOptionById(optionId: string): Promise<TenantServiceOption> {
    const response = await api.get<TenantServiceOption>(`/tenants/service-options/${optionId}`);
    return response.data;
  }

  // Create new tenant service option
  async createTenantServiceOption(
    optionData: TenantServiceOptionData & { tenantServiceId: string }
  ): Promise<TenantServiceOption> {
    const response = await api.post<TenantServiceOption>("/tenants/service-options", optionData);
    return response.data;
  }

  // Update tenant service option by ID
  async updateTenantServiceOptionById(
    optionId: string,
    optionData: Partial<TenantServiceOptionData>
  ): Promise<TenantServiceOption> {
    const response = await api.put<TenantServiceOption>(
      `/tenants/service-options/${optionId}`,
      optionData
    );
    return response.data;
  }

  // Delete tenant service option by ID
  async deleteTenantServiceOptionById(optionId: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/tenants/service-options/${optionId}`);
    return response.data;
  }

  // Bulk operations for tenant service options
  async bulkTenantServiceOptionsOperation(bulkData: {
    operation: 'create' | 'update' | 'delete';
    options: Array<TenantServiceOptionData & { id?: string; tenantServiceId: string }>;
  }): Promise<{ success: string[]; failed: Array<{ id?: string; key?: string; error: string }> }> {
    const response = await api.post<{
      success: string[];
      failed: Array<{ id?: string; key?: string; error: string }>;
    }>("/tenants/service-options/bulk", bulkData);
    return response.data;
  }
}

// Create and export singleton instance
export const tenantService = new TenantService();

// Export individual methods for convenience
export const {
  createTenant,
  getTenants,
  getTenantHierarchy,
  getTenantById,
  getTenantUsers,
  getTenantServices,
  getTenantDocuments,
  getTenantDocumentCount,
  updateTenant,
  deleteTenant,
  assignUserToTenant,
  removeUserFromTenant,
  assignServiceToTenant,
  removeServiceFromTenant,
  bulkTenantOperation,
  cloneTenant,
  getTenantsByBrand,
  getActiveTenants,
  getTenantChildren,
  websiteOrigination,
  // New service options methods
  getTenantServiceOptions,
  updateTenantService,
  upsertTenantServiceOption,
  deleteTenantServiceOption,
  // Tenant service CRUD methods
  createTenantService,
  getAllTenantServices,
  getTenantServiceById,
  updateTenantServiceById,
  deleteTenantServiceById,
  // Tenant service options CRUD methods
  getAllTenantServiceOptions,
  getTenantServiceOptionById,
  createTenantServiceOption,
  updateTenantServiceOptionById,
  deleteTenantServiceOptionById,
  bulkTenantServiceOptionsOperation,
} = tenantService;
